import React from 'react';
import { Box, Flex, Text, IconButton, FlexProps } from '@chakra-ui/react';
import { IoAdd, IoLockClosedOutline, IoRemove } from 'react-icons/io5';

import { toDatetime, toMoney } from 'src/legacy/Utils/Conversor';
import { CartItemType } from 'src/types/ui';
import { TooltipHelperIcon } from 'src/Pages/EventManagement/components';

const getPriceLabel = (price: number, taxPrice: number) =>
  taxPrice ? `${toMoney(price)} + taxa.` : toMoney(price);

const Controls: React.FC<{
  quantity: number;
  onRemove: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isClosed: boolean;
  isSoldOff: boolean;
  isQuantityAvailable: boolean;
}> = ({ quantity, onRemove, isClosed, isSoldOff, isQuantityAvailable }) => {
  if (isClosed) {
    return <Text color="gray.400">ENCERRADO</Text>;
  }

  if (isSoldOff) {
    return <Text color="gray.400">ESGOTADO</Text>;
  }

  return (
    <Flex ml="auto" align="center">
      {quantity > 0 && (
        <>
          <IconButton
            icon={<IoRemove />}
            size="sm"
            fontSize="20px"
            variant="ghost"
            colorScheme="gray"
            color="brand.500"
            onClick={onRemove}
            aria-label="Diminuir quantidade"
          />
          <Text fontSize="xl" minW="40px" textAlign="center">
            {quantity}
          </Text>
        </>
      )}
      <IconButton
        icon={<IoAdd />}
        size="sm"
        fontSize="20px"
        variant="ghost"
        colorScheme="gray"
        color="brand.500"
        isDisabled={!isQuantityAvailable}
        aria-label="Aumentar quantidade"
      />
    </Flex>
  );
};

export type CartItemOnAddParams = {
  event: React.MouseEvent<HTMLDivElement>;
  cartItem: CartItemType;
};

export type CartItemOnRemoveParams = {
  event: React.MouseEvent<HTMLButtonElement>;
  cartItem: CartItemType;
};

type Props = {
  cartItem: CartItemType;
  onAdd?: (params: CartItemOnAddParams) => void;
  onRemove?: (params: CartItemOnRemoveParams) => void;
} & FlexProps;

export const CartItem = ({ cartItem, onAdd, onRemove, ...props }: Props) => {
  const isClosed = new Date() > toDatetime(cartItem.sellEnd);
  const isSoldOff = cartItem.availableQuantity === 0;
  const isQuantityAvailable = cartItem.quantity < cartItem.availableQuantity;
  const canAdd = !isClosed && !isSoldOff && isQuantityAvailable;
  const isPrivated = cartItem.restricted;

  const _onAdd = (event: React.MouseEvent<HTMLDivElement>) => {
    if (canAdd) {
      onAdd?.({ event, cartItem });
    }
  };

  const _onRemove = (event: React.MouseEvent<HTMLButtonElement>) => {
    onRemove?.({ event, cartItem });
  };

  return (
    <Flex
      py={3}
      w='full'
      cursor={canAdd ? 'pointer' : 'default'}
      align="center"
      flexGrow="1"
      justify="space-between"
      onClick={_onAdd}
      userSelect="none"
      {...props}
    >
      <Box>
        {isPrivated ? 
        <>
        {
          cartItem.restricted &&
            (
              <Flex gap={1} bg='#4ac580' w='fit-content' p='5px' rounded='md' fontSize='11px' color='white' mb='1'>
                <IoLockClosedOutline /> Liberado link
              </Flex>
            )
          }
        <Text
          wordBreak="break-word"
          fontWeight="bold"
          fontSize="lg"
          lineHeight="normal">
          {cartItem.name}
          {cartItem.description && (
            <TooltipHelperIcon
              display="initial"
              textAlign="center"
              verticalAlign="middle"
              label={cartItem.description}
              iconProps={{ color: '#1FA9FF' }}
              ml="1"
              color="blue"
              placementTopStart={true}
            />
          )}
        </Text>

        <Text pt={1} lineHeight={'normal'} color="green.600">
          {cartItem.price > 0 ? (
            <>{getPriceLabel(cartItem.price, cartItem.taxPrice)}</>
          ) : (
            <>Gratuito</>
          )}
        </Text>
        </>
        :
        <>
        <Text
          wordBreak="break-word"
          fontWeight="bold"
          fontSize="lg"
          lineHeight="normal">
          {cartItem.name}
          {cartItem.description && (
            <TooltipHelperIcon
              display="initial"
              textAlign="center"
              verticalAlign="middle"
              label={cartItem.description}
              iconProps={{ color: '#1FA9FF' }}
              ml="1"
              color="blue"
              placementTopStart={true}
            />
          )}
        </Text>

        <Text pt={1} lineHeight={'normal'} color="green.600">
          {cartItem.price > 0 ? (
            <>{getPriceLabel(cartItem.price, cartItem.taxPrice)}</>
          ) : (
            <>Gratuito</>
          )}
        </Text>
        </>
        }
      </Box>
      <Controls
        quantity={cartItem.quantity}
        onRemove={_onRemove}
        {...{ isClosed, isSoldOff, isQuantityAvailable }}
      />
    </Flex>
  );
};
